import TextInput from 'components/Common/Form/Input/TextInput'
import TextButton from 'LuxKit/Button/TextButton'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { formToObject } from 'lib/forms/formToObject'
import { rem } from 'polished'
import type { FormEventHandler } from 'react'
import { useCallback, useContext, useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { fetchAgentAccountByAgentId, onboardAgency, setAgencyDetails } from 'actions/AgentHubActions'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import TextLink from 'LuxKit/TextLink'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { updateUserDetails } from 'actions/AuthActions'
import AgentHubAddressInputFields from 'agentHub/components/AgentHubAddressInputFields'
import { FormProvider, useForm } from 'react-hook-form'
import GeoContext from 'contexts/geoContext'
import type { SetAgencyDetailsParams } from 'agentHub/api/requestPostSetAgencyDetails'
import { validateBusinessIdentifier, getAgencyCodeInfoFromCountryCode } from '../utils/agencyCodeUtils'
import config from 'constants/config'
import AgentHubAccountSetupStepIndicator from 'agentHub/components/AgentHubAccountSetupStepIndicator/AgentHubAccountSetupStepIndicator'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import { isAgentHubAffiliationRequired } from 'agentHub/selectors/agentHubSelectors'
import { isAgencyIdentifierRequired } from 'lib/agentHub/agentHubUtils'

const InputGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${rem(12)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr 1fr;
  }
`

const FullWidthTextInput = styled(TextInput)`
  grid-column: 1 / -1;
`

export interface FormData {
  name: string
  agencyIdentifier: string
  affiliation: string
  streetAddress: string
  city: string
  stateOrTerritory: string
  postcode: string
  country: string
}

interface Props {
  agentId: string
}

function AgentHubCompleteAgencyDetailsStep(props: Props) {
  const { agentId } = props
  const { account, fetching } = useAppSelector(state => state.agentHub.agentHubAccount)
  const { currentRegionCode } = useContext(GeoContext)
  const intl = useIntl()
  const methods = useForm<Props>()
  const formRef = useRef<HTMLFormElement>(null)
  const identityInputRef = useRef<HTMLInputElement>(null)
  const appDispatch = useAppDispatch()
  const loading = useAppSelector(state => state.agentHub.isSavingAgencyDetails)
  const registerError = useAppSelector(state => state.agentHub.savingAgencyDetailsError)
  const authAccount = useAppSelector(state => state.auth.account)

  const agencyIdentifier = getAgencyCodeInfoFromCountryCode(currentRegionCode)

  const isCommissionsPayoutStepEnabled = config.AGENT_HUB_COLLECT_PAYOUT_DETAILS_ENABLED

  useEffect(() => {
    if (agentId && !account && !fetching) {
      appDispatch(fetchAgentAccountByAgentId(agentId))
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId, account, fetching])

  useEffect(() => {
    if (authAccount.memberId) {
      const name = account?.name.split(' ')
      const givenName = name?.[0] || ''
      const surname = name?.[1] || ''
      const fullName = `${givenName} ${surname}`
      appDispatch(updateUserDetails({
        givenName,
        surname,
        fullName,
      }))
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [account, appDispatch])

  const onABNChange = useCallback(() => {
    identityInputRef.current?.setCustomValidity('')
    formRef.current?.checkValidity()
  }, [])

  const errorMessage = registerError?.message
    ? intl.formatMessage(
        {
          id: 'B4c-x_jehVibRgGHjJRW',
          defaultMessage: 'Error message: {errorDetail} Something went wrong while setting up your registration.',
          description: 'Registration error message with specific error detail from server',
        },
        { errorDetail: registerError.message },
      )
    : intl.formatMessage({
        id: 'Myhqwtn6yt9lv8iPr_1B',
        defaultMessage: 'Something went wrong while setting up your registration.',
        description: 'Generic registration error message in the agent hub portal',
      })

  const handleSubmit = useCallback<FormEventHandler<HTMLFormElement>>(
    (event) => {
      event.preventDefault()
      const formData = formToObject<FormData>(event.currentTarget)
      const isBusinessIdentifierValid = isAgencyIdentifierRequired(currentRegionCode) ?
          validateBusinessIdentifier(currentRegionCode, formData.agencyIdentifier) :
        true
      if (!isBusinessIdentifierValid) {
        identityInputRef.current?.setCustomValidity(intl.formatMessage({
          id: 'Z5A3iXFWNm4dEwZE027K',
          defaultMessage: 'Please enter a valid {label}',
          description: 'Agency identifier validation error message when an issue occurs',
        }, {
          label: agencyIdentifier.label,
        }))
        formRef.current?.checkValidity()
        return
      }

      const agencyDetails: SetAgencyDetailsParams = {
        name: formData.name,
        businessCodeValue: formData.agencyIdentifier || '',
        streetAddress: formData.streetAddress,
        city: formData.city,
        stateOrTerritory: formData.stateOrTerritory,
        postcode: formData.postcode,
        regionCode: currentRegionCode as App.AgentHubEnabledRegions,
        country: formData.country,
        agentId,
      }

      appDispatch(setAgencyDetails(agencyDetails))
    },
    [agentId, appDispatch, currentRegionCode, agencyIdentifier.label, intl],
  )

  useEffect(() => {
    if (account?.agencyId && account.status === 'pending') {
      if (!isCommissionsPayoutStepEnabled) {
        appDispatch(onboardAgency(agentId))
      }

      if (!authAccount.givenName) {
        appDispatch(updateUserDetails({
          givenName: account?.name.split(' ')[0] || '',
          surname: account?.name.split(' ')[1] || '',
        }))
      }
    }
  }, [account?.name, agentId, appDispatch, authAccount.givenName, isCommissionsPayoutStepEnabled, account?.agencyId, account?.status])

  const AgencyIdentifierInput = isAgentHubAffiliationRequired ? TextInput : FullWidthTextInput

  return (
    <ModalBody>
      <ModalContent>
        <VerticalSpacer gap={20}>
          <AgentHubAccountSetupStepIndicator currentStep="agentHubAccount" />
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit} ref={formRef}>
              <Group direction="vertical" gap={24}>
                <LoadingIndicator visible={loading || !account || fetching} floating />
                <InputGrid>
                  <FullWidthTextInput
                    label={intl.formatMessage({
                      id: '-MgTuxmoeCbFBNbe68mV',
                      defaultMessage: 'Agency name',
                      description: 'Agency name input label in agent hub agency details setup form',
                    })}
                    name="name"
                    value={account?.agencyName || ''}
                    required
                  />
                  {isAgentHubAffiliationRequired && <TextInput
                    label={intl.formatMessage({
                      id: 'Qzj6TDmq04gvaYam38Gq',
                      defaultMessage: 'Affiliation',
                      description: 'Affiliation input label in agent hub agency details setup form',
                    })}
                    name="affiliation"
                    value={account?.affiliation || ''}
                    required
                  />}
                  <AgencyIdentifierInput
                    label={agencyIdentifier.label}
                    type="number"
                    name="agencyIdentifier"
                    minLength={11}
                    maxLength={11}
                    pattern="^(\d *?){11}$"
                    placeholder={intl.formatMessage({
                      id: 'RIQA-8K8nmEjUgmbZMlS',
                      defaultMessage: 'Enter your {label}',
                      description: 'Agency identifier input placeholder in the agent hub portal',
                    }, {
                      label: agencyIdentifier.label,
                    })}
                    required={isAgencyIdentifierRequired(currentRegionCode)}
                    requiredErrorMessage={intl.formatMessage({
                      id: '5vJQaOpKKhrA8xbN9TEW',
                      defaultMessage: '{label} is required',
                      description: 'Agency identifier required error message when an issue occurs',
                    }, {
                      label: agencyIdentifier.label,
                    })}
                    ref={identityInputRef}
                    onChange={onABNChange}
                    helpText={
                      <TextLink size="caption" weight="regular" href={agencyIdentifier.lookupUrl} target="_blank">
                        {intl.formatMessage({
                          id: 'GwUuNFa4NsbAFSw6N9tW',
                          defaultMessage: 'Search for your {label} on the register',
                          description: 'Agency identifier lookup link text in the search and discovery interface',
                        }, {
                          label: agencyIdentifier.label,
                        })}
                      </TextLink>
                    }
                  />
                  <AgentHubAddressInputFields />
                </InputGrid>

                <VerticalSpacer gap={12}>
                  {!!registerError && <MessageBanner kind="critical">{errorMessage}</MessageBanner>}
                  <TextButton
                    fit="full-width"
                    kind="primary"
                    shape="square"
                    size="medium"
                    type="submit"
                  >
                    {intl.formatMessage({
                      id: 'gkHtWqMEX-EhHamYVK1v',
                      defaultMessage: 'Continue',
                      description: 'Continue button in agent hub agency details setup form',
                    })}
                  </TextButton>
                </VerticalSpacer>
              </Group>
            </form>
          </FormProvider>
        </VerticalSpacer>
      </ModalContent>
    </ModalBody>
  )
}

export default AgentHubCompleteAgencyDetailsStep
